'use client';

import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// Reuses the price tooltip's look so both read as the same component.
import tooltipStyles from './ScrollablePriceText.module.scss';
interface TruncatedTextProps {
  text: string;
  /** Owns the clamping/ellipsis — this component only measures it. */
  className?: string;
}

/**
 * Text that truncates via CSS and reveals the full string in a styled tooltip
 * on hover, but only when it is actually cut off.
 *
 * Unlike `ScrollablePriceText` this does not translate the content, so the
 * caller keeps `text-overflow: ellipsis` / `-webkit-line-clamp`. The marquee
 * approach cancels both, which is why the price component isn't reused here.
 */
const TruncatedText: FC<TruncatedTextProps> = ({
  text,
  className
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Both axes matter: `text-overflow: ellipsis` on a nowrap line
    // overflows horizontally, while `-webkit-line-clamp` lets the text
    // wrap and clips it vertically — checking width alone misses the
    // clamped case entirely.
    setIsTruncated(el.scrollWidth - el.clientWidth > 1 || el.scrollHeight - el.clientHeight > 1);
  }, []);
  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.parentElement) observer.observe(el.parentElement);
    return () => observer.disconnect();
  }, [measure, text]);
  const showTooltip = () => {
    if (!isTruncated || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom,
      left: rect.left + rect.width / 2
    });
  };
  const hideTooltip = () => setTooltipPos(null);
  return <>
            <span ref={ref} className={className} dir="auto" onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
                {text}
            </span>
            {tooltipPos && createPortal(<span className={tooltipStyles.tooltip} style={{
      top: tooltipPos.top,
      left: tooltipPos.left
    }}>
                        {text}
                    </span>, document.body)}
        </>;
};
export default TruncatedText;